import { useRef } from 'react';
import type { MouseEvent } from 'react';
import { useAtomValue } from 'jotai';
import { cn } from '@rocco/utils/cn';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import { TileMedia } from '@rocco/ui/tile-shared/components/TileMedia';
import { TileBadge } from '@rocco/ui/tile-shared/components/TileBadge';

import { TileFollowButton } from '@rocco/ui/tile-shared/components/TileFollowButton';
import { uiContextAtomTileActions } from '@rocco/ui/tile-shared/hooks/use-shared-tiles';
import { uiContextAtomViewTransitionsEnabled } from '@rocco/states/uiContextAtomViewTransitions';
import { useTileAppModeScope } from '@rocco/contexts/TileAppModeScopeContext';
import { useTracker } from '../../../../../tracker';
import { trackTileMediaDetailClick } from '../../../../../web/app/modules/tracking/util/trackTileMedia';
import { OptionalLink } from '@rocco/components/link/OptionalLink';
import { TILE_COMPONENTS_CLASS_NAMES } from '@rocco/ui/tile-shared/utils/contstants';
import {
    PRODUCT_HERO_TRANSITION_NAME,
    recordTileMorphStart,
    startsPlainNavigation,
    usePrefersReducedMotion,
    useTileHeroViewTransitionStyle,
} from '@rocco/ui/tile-shared/hooks/use-tile-hero-view-transition';

export const ProductTile = (props: TileViewModel<ProductTileViewModel>) => {
    const {
        aspectRatio,
        model: product,
        section,
        disableDesignBoard,
        enableTileSlider,
        isFollowed,
        onFollow,
        hideFollowButton = false,
        followButton,
        overlay,
        inlineActionButtons,
        disableLink = false,
        onClick,
        className,
        contentEditable,
        eager,
        rounded = true,
        imageSizes,
    } = props;
    // 5.0 directory browse: these tiles carry their own typography and text
    // colours rather than the default tile's. Keyed off the media aspect, so
    // every frame the browse grids use has to be listed here — a grid that
    // switches to an unlisted one silently falls back to the default styling.
    const isPortrait =
        aspectRatio === 'portrait' || aspectRatio === 'productTall';
    const tileActions = useAtomValue(uiContextAtomTileActions);
    const tileRef = useRef<HTMLAnchorElement>(null);
    const tracker = useTracker();
    // The morph runs wherever a tile does — the flag is its only gate, so a
    // related tile on a detail page zooms the same way a directory tile
    // does. The surface scope is narrower and covers only the mobile app-mode
    // chrome below: that stays a listing-to-detail presentation, so moving
    // between detail pages does not bury the site header behind a back stack.
    // Both hooks run unconditionally (no short-circuit) to keep the hook
    // order stable.
    const viewTransitionsFlagOn = useAtomValue(
        uiContextAtomViewTransitionsEnabled,
    );
    const inAppModeScope = useTileAppModeScope();
    // Reduced motion opts out of the whole flow — no view transition (no
    // snapshot capture at all) and no app-mode chrome; the page behaves
    // exactly like a plain flag-off navigation.
    const prefersReducedMotion = usePrefersReducedMotion();
    const appModeEnabled =
        viewTransitionsFlagOn && inAppModeScope && !prefersReducedMotion;
    const viewTransitionsEnabled =
        viewTransitionsFlagOn && !prefersReducedMotion;
    const heroStyle = useTileHeroViewTransitionStyle(
        disableLink || !viewTransitionsEnabled ? undefined : product.link,
    );

    const draftOrPending = product.draft || product.pendingApproval;
    const statusLabel = draftOrPending
        ? product.draft
            ? 'Draft'
            : 'Pending Approval'
        : product.badge;

    const handleTileClick = (event?: MouseEvent<HTMLAnchorElement>) => {
        onClick?.();
        if (disableLink) {
            return;
        }

        if (
            viewTransitionsEnabled &&
            (!event || startsPlainNavigation(event))
        ) {
            recordTileMorphStart(
                PRODUCT_HERO_TRANSITION_NAME,
                product.link,
                tileRef.current?.querySelector('img')?.currentSrc,
            );
        }

        tileActions?.trackingConfig?.onClick?.(
            tileRef,
            product.id,
            product.professional.id,
            section,
        );

        trackTileMediaDetailClick(tracker, {
            itemID: product.id,
            itemType: 'ProductTile',
            itemName: product.title,
        });
    };

    // Media and title link to the product; the supplier links to its own
    // profile. They are siblings rather than nested, because an anchor inside
    // an anchor is invalid and browsers recover from it unpredictably — the
    // inner one frequently stops being clickable at all.
    const productLinkProps = {
        mode: (disableLink ? 'override' : 'normal') as 'override' | 'normal',
        to: product.link,
        prefetch: 'intent' as const,
        onClick: handleTileClick,
    };

    return (
        <div
            className={cn(
                'flex h-full flex-col',
                isPortrait ? 'gap-2.5' : 'gap-4',
                className,
            )}
        >
            <OptionalLink
                ref={tileRef}
                {...productLinkProps}
                viewTransition={viewTransitionsEnabled}
                state={appModeEnabled ? { detailTransition: true } : undefined}
            >
                {/* The morphing box for the tile→detail view transition. The
                    hover/save overlays inside are part of the snapshot, which
                    is fine — the new page's crop dissolves over it early in
                    the flight. */}
                <div className="group relative h-auto" style={heroStyle}>
                    <TileMedia
                        aspectRatio={aspectRatio}
                        images={product.images}
                        video={product.video}
                        enableTileSlider={enableTileSlider}
                        rounded={rounded}
                        eager={eager}
                        sizes={imageSizes}
                        onSlideChange={(direction) => {
                            tileActions?.trackingConfig?.onTileMediaSliderSlideChange?.(
                                direction,
                                product.id,
                            );
                        }}
                    />

                    {overlay ? (
                        overlay
                    ) : (
                        <>
                            <div
                                className={cn(
                                    // Stacks with TileOverlay's own scrim on
                                    // tiles that have one, so keep it faint.
                                    'absolute inset-0 hidden bg-gray-900/5 opacity-0 transition-opacity duration-300 md:block md:group-hover:opacity-100',
                                    rounded && 'rounded-tile',
                                )}
                            />
                            <div className="absolute top-3.5 right-4 flex gap-1 transition-opacity duration-300 md:invisible md:opacity-0 md:group-hover:visible md:group-hover:opacity-100">
                                {followButton
                                    ? followButton
                                    : !hideFollowButton &&
                                      !disableDesignBoard && (
                                          <TileFollowButton
                                              pinId={product.id}
                                              pinType="ProductPin"
                                              isFollowed={isFollowed}
                                              onFollow={onFollow}
                                          />
                                      )}
                            </div>
                        </>
                    )}

                    {/* Badge max-w keeps it clear of the 42px save control at
                        2-up mobile widths; Badge ellipsises natively. */}
                    {statusLabel && (
                        <div className="absolute top-4 left-4 flex max-md:max-w-[calc(100%-5rem)]">
                            <TileBadge title={statusLabel} />
                        </div>
                    )}
                </div>
            </OptionalLink>

            <div
                className={cn(
                    'flex flex-col gap-4',
                    isPortrait && 'max-md:gap-0',
                )}
            >
                {inlineActionButtons && (
                    <div className="flex flex-col">{inlineActionButtons}</div>
                )}
                <div
                    className={cn(
                        TILE_COMPONENTS_CLASS_NAMES.TILE_DESCRIPTION_BLOCK,
                        'flex flex-col',
                    )}
                >
                    {/* Editing modes render the text bare: a link wrapper
                            would swallow the clicks that place the caret. */}
                    <OptionalLink
                        {...productLinkProps}
                        mode={
                            contentEditable?.enabled.title
                                ? 'disable'
                                : productLinkProps.mode
                        }
                        className="min-w-0"
                    >
                        <span
                            contentEditable={contentEditable?.enabled.title}
                            role={
                                contentEditable?.enabled.title
                                    ? 'textbox'
                                    : undefined
                            }
                            aria-label={
                                contentEditable?.enabled.title
                                    ? 'Edit title'
                                    : undefined
                            }
                            aria-multiline="false"
                            suppressContentEditableWarning={true}
                            onInput={(e: React.FormEvent<HTMLSpanElement>) =>
                                contentEditable?.onChange?.(
                                    (e.target as HTMLSpanElement).textContent ??
                                        '',
                                    'title',
                                )
                            }
                            onBlur={() => {
                                contentEditable?.onBlur?.();
                            }}
                            onKeyDown={(
                                e: React.KeyboardEvent<HTMLSpanElement>,
                            ) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    (e.target as HTMLSpanElement).blur();
                                }
                            }}
                            className={cn(
                                isPortrait
                                    ? // Below md, match the compact design-board
                                      // tile title scale (text-4.5/leading-none).
                                      // md+ is 16px, matching the browse chrome
                                      // (pills, heading, Sort) around the grid.
                                      'overflow-hidden font-medium text-ellipsis text-[#323232] max-md:text-[0.75rem] max-md:leading-[1.2] md:text-[1rem] md:leading-[1.3]'
                                    : 'overflow-hidden text-sm font-medium tracking-[0.03rem] text-ellipsis md:text-base md:tracking-[0.02rem]',
                                contentEditable?.enabled.title
                                    ? 'truncate'
                                    : isPortrait
                                      ? // On the narrow 2-up mobile browse grid,
                                        // let the title wrap to 2 lines instead of
                                        // truncating; reserve 2 lines' height so
                                        // rows stay aligned whether it runs 1 or 2
                                        // lines (2 × 1.125rem × 1 leading-none).
                                        // md+ keeps the single-line clamp.
                                        'max-md:line-clamp-2 max-md:min-h-[1.8rem] md:line-clamp-1'
                                      : 'line-clamp-1',
                                contentEditable?.enabled.title &&
                                    'focus-within:border-primary-500 -mx-2 cursor-text rounded-md border border-transparent px-2 focus-within:bg-gray-100 hover:border-gray-500',
                            )}
                        >
                            {product.title}
                        </span>
                    </OptionalLink>
                    {/* The supplier is its own destination: from a grid of
                            products, the brand behind one is a route people
                            take as often as the product itself. */}
                    <OptionalLink
                        mode={
                            contentEditable?.enabled.description ||
                            disableLink ||
                            !product.professional.link
                                ? 'disable'
                                : 'normal'
                        }
                        to={product.professional.link}
                        prefetch="intent"
                        className="min-w-0"
                    >
                        <span
                            contentEditable={
                                contentEditable?.enabled.description
                            }
                            role={
                                contentEditable?.enabled.description
                                    ? 'textbox'
                                    : undefined
                            }
                            aria-label={
                                contentEditable?.enabled.description
                                    ? 'Edit description'
                                    : undefined
                            }
                            aria-multiline="false"
                            suppressContentEditableWarning={true}
                            onInput={(e: React.FormEvent<HTMLSpanElement>) =>
                                contentEditable?.onChange?.(
                                    (e.target as HTMLSpanElement).textContent ??
                                        '',
                                    'description',
                                )
                            }
                            onBlur={() => {
                                contentEditable?.onBlur?.();
                            }}
                            onKeyDown={(
                                e: React.KeyboardEvent<HTMLSpanElement>,
                            ) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    (e.target as HTMLSpanElement).blur();
                                }
                            }}
                            className={cn(
                                isPortrait
                                    ? // Quiet meta scale below md, matching the
                                      // design-board card's "Created by" grey;
                                      // md+ tracks the title size (16px).
                                      'overflow-hidden font-normal text-ellipsis max-md:text-[0.75rem] max-md:leading-[1.3] max-md:text-gray-500 md:text-[1rem] md:leading-[1.3] md:text-[#767676]'
                                    : 'overflow-hidden text-sm font-medium tracking-[0.03rem] text-ellipsis text-gray-900/40 md:text-base md:tracking-[0.02rem]',
                                // Darkens to the page ink on hover, so the name
                                // reads as its own target rather than as the
                                // product's caption.
                                !contentEditable?.enabled.description &&
                                    !disableLink &&
                                    product.professional.link &&
                                    'transition-colors hover:text-[#323232] md:hover:text-[#323232]',
                                !contentEditable?.enabled.description &&
                                    'line-clamp-1',
                                contentEditable?.enabled.description &&
                                    'focus-within:border-primary-500 -ml-2 cursor-text rounded-md border border-transparent px-2 focus-within:bg-gray-100 hover:border-gray-500',
                            )}
                        >
                            {product.professional.name}
                        </span>
                    </OptionalLink>
                </div>

                {/* Price and colour swatches are deliberately not shown on
                    the tile: the tile carries the product and its supplier,
                    and price/variants belong to the detail page. */}
            </div>
        </div>
    );
};
